import { apiFetch } from './client.js';

// Типы для маркетплейса — временная локальная копия, пока @app/shared
// не экспортирует их с префиксом Marketplace.
export type MarketplaceListingKind = 'agent' | 'mcp' | 'webhook' | 'pipeline';
export type MarketplaceListingStatus = 'draft' | 'pending' | 'published' | 'rejected' | 'disabled';
export interface MarketplaceManifest {
  kind: MarketplaceListingKind;
  schema_version: number;
  primaryAgentRef?: string;
  agents?: any[];
  webhooks?: any[];
  mcp?: any[];
  skills?: any[];
  requirements?: any[];
  trigger?: any;
  response?: any;
  knowledge?: any;
  test?: any;
}
export interface RequirementInputs {
  [ref: string]: string;
}

export interface AgentOverride {
  name?: string;
  description?: string;
  system_prompt?: string;
  model_tier?: 'brain' | 'mid' | 'cheap';
  enabled_builtins?: string[];
}

export type AgentOverrides = Record<string, AgentOverride>;

export interface BuiltinCatalogItem {
  id: string;
  label: string;
  description: string;
  aliases?: string[];
}

export interface OrchestratorQuestion {
  id: string;
  text: string;
  options: string[];
}

export interface OrchestratorClarifyResult {
  ok: boolean;
  source: 'llm';
  status: 'questions' | 'ready';
  questions: OrchestratorQuestion[];
  message?: string;
  modelTier?: 'brain' | 'mid' | 'cheap';
  pendingServices?: Array<{ id: string; name: string }>;
}

export interface OrchestratedManifestResult {
  ok: boolean;
  source: 'llm' | 'fallback';
  // После сборки сервер может переспросить: status='questions' + что именно неясно.
  status?: 'ready' | 'questions';
  message?: string;
  problems?: string[];
  questions?: OrchestratorQuestion[];
  manifest: MarketplaceManifest;
  requirements: Array<{ ref: string; label: string; type: string; required: boolean; description?: string | null; default?: string | number | null }>;
  accountChoices?: Array<{ provider: string; providerLabel: string; options: Array<{ id: string; label: string }> }>;
  pendingServices?: Array<{ id: string; name: string }>;
  connectedServices?: Array<{ id: string; name: string }>;
  warnings?: string[];
  fallbackReason?: string | null;
  modelTier?: 'brain' | 'mid' | 'cheap' | null;
}

// Подсказка в конструкторе автоматизаций: мини-чат, который знает контекст
// текущей автоматизации и помогает понять, где взять URL/токен для
// конкретного внешнего сервиса при заполнении полей "Настройте под себя".
export interface SetupAssistantRequirement {
  ref: string;
  label: string;
  type: string;
  description?: string | null;
}

export interface SetupAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface SetupAssistantResult {
  ok: boolean;
  reply: string;
  modelTier?: 'brain' | 'mid' | 'cheap';
}

export interface PublicListingCard {
  id: string; kind: MarketplaceListingKind; slug: string; title: string; summary: string;
  icon: string | null; category: string; tags: string[]; is_system: boolean;
  status: MarketplaceListingStatus; version: number; installs_count: number;
  author_telegram_id: number; created_at: string; published_at: string;
}

export interface DetailListingCard extends PublicListingCard {
  description: string;
  requirements: any[];
  manifest_summary: any;
  rejected_reason?: string | null;
}

// Куда вести пользователя при клике на карточку установки — на реально
// созданный ресурс (сценарий/агент/mcp/вебхук/навык/документ).
export type InstallPrimaryResourceType = 'pipeline' | 'agent' | 'mcp' | 'webhook' | 'skill' | 'document';
export interface InstallPrimaryResource {
  type: InstallPrimaryResourceType;
  id: string;
}
export interface InstallResourceCounts {
  agents: number;
  webhooks: number;
  mcp_servers: number;
  skills: number;
  pipelines: number;
  documents: number;
}
// Карточка установленного листинга (GET /api/marketplace/installs) —
// содержит реальные title/kind/icon исходного листинга (JOIN на бэкенде),
// а не просто факт установки, плюс подсказку куда вести при клике.
export interface InstallCard {
  id: string;
  listing_id: string;
  title: string;
  kind: MarketplaceListingKind | null;
  icon: string | null;
  slug: string | null;
  listing_status: MarketplaceListingStatus | null;
  installed_version: number;
  status: 'installed' | 'removed' | 'error';
  error: string | null;
  created_at: string;
  updated_at: string;
  resourceCounts: InstallResourceCounts;
  primaryResource: InstallPrimaryResource | null;
}

export const marketplaceApi = {
  getMe: () => apiFetch<{ userId: number; admin: boolean }>('/api/marketplace/me'),
  getListings: (params?: { kind?: MarketplaceListingKind; category?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.kind) query.set('kind', params.kind);
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return apiFetch<PublicListingCard[]>(`/api/marketplace/listings${qs ? '?' + qs : ''}`);
  },
  getListing: (id: string) => apiFetch<DetailListingCard>(`/api/marketplace/listings/${id}`),
  getMine: () => apiFetch<DetailListingCard[]>('/api/marketplace/mine'),
  getInstalls: () => apiFetch<InstallCard[]>('/api/marketplace/installs'),
  installListing: (id: string, inputs: RequirementInputs, agentOverrides?: AgentOverrides) => apiFetch<{ok: boolean; installId: string; created: any; pipelineId?: string}>(`/api/marketplace/listings/${id}/install`, {
    method: 'POST',
    body: JSON.stringify({ inputs, agentOverrides: agentOverrides ?? {} }),
  }),
  orchestrateClarify: (data: { prompt: string; kind?: MarketplaceListingKind }) => apiFetch<OrchestratorClarifyResult>('/api/marketplace/orchestrate-clarify', {
    method: 'POST',
    body: JSON.stringify(data),
    _timeoutMs: 300_000,
  } as any),
  orchestrateManifest: (data: { prompt: string; kind?: MarketplaceListingKind; skipReview?: boolean }) => apiFetch<OrchestratedManifestResult>('/api/marketplace/orchestrate-manifest', {
    method: 'POST',
    body: JSON.stringify(data),
    _timeoutMs: 300_000,
  } as any),
  installManifest: (data: { manifest: MarketplaceManifest; inputs?: RequirementInputs; agentOverrides?: AgentOverrides; title?: string; summary?: string; accountSelections?: Record<string, string> }) => apiFetch<{ok: boolean; installId: null; created: any; pipelineId?: string; pipelineToken?: string; trigger_url?: string}>('/api/marketplace/install-manifest', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getBuiltins: () => apiFetch<{ builtins: BuiltinCatalogItem[] }>('/api/marketplace/builtins'),
  setupAssistant: (data: { automationTitle: string; automationSummary?: string; requirements: SetupAssistantRequirement[]; messages: SetupAssistantMessage[] }) => apiFetch<SetupAssistantResult>('/api/marketplace/setup-assistant', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  resolveTelegramChatId: (bot_token: string) => apiFetch<{ ok: boolean; chats: Array<{ id: string; title: string; type: string }>; hint?: string | null }>('/api/marketplace/telegram/resolve-chat-id', { method: 'POST', body: JSON.stringify({ bot_token }) }),
  deleteInstall: (id: string) => apiFetch<{removed: boolean; id: string}>(`/api/marketplace/installs/${id}`, { method: 'DELETE' }),
  createListing: (data: any) => apiFetch<DetailListingCard>('/api/marketplace/listings', { method: 'POST', body: JSON.stringify(data) }),
  submitListing: (id: string) => apiFetch<DetailListingCard>(`/api/marketplace/listings/${id}/submit`, { method: 'POST' }),
  updateListing: (id: string, data: any) => apiFetch<DetailListingCard>(`/api/marketplace/listings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteListing: (id: string) => apiFetch<{deleted: boolean; id: string}>(`/api/marketplace/listings/${id}`, { method: 'DELETE' }),
  exportAgentManifest: (agentId: string) => apiFetch<{manifest: MarketplaceManifest}>(`/api/marketplace/export/agent/${agentId}`),
  exportPipelineManifest: (pipelineId: string) => apiFetch<{manifest: MarketplaceManifest}>(`/api/marketplace/export/pipeline/${pipelineId}`),
};

export const adminMarketplaceApi = {
  getPending: () => apiFetch<any[]>('/api/admin/marketplace/pending'),
  getListings: (status?: MarketplaceListingStatus) => apiFetch<any[]>(`/api/admin/marketplace/listings${status ? '?status=' + status : ''}`),
  getListing: (id: string) => apiFetch<any>(`/api/admin/marketplace/listings/${id}`),
  getEvents: (id: string) => apiFetch<any[]>(`/api/admin/marketplace/listings/${id}/events`),
  approve: (id: string) => apiFetch<any>(`/api/admin/marketplace/listings/${id}/approve`, { method: 'POST' }),
  reject: (id: string, reason: string) => apiFetch<any>(`/api/admin/marketplace/listings/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  requestChanges: (id: string, reason: string) => apiFetch<any>(`/api/admin/marketplace/listings/${id}/request-changes`, { method: 'POST', body: JSON.stringify({ reason }) }),
  disable: (id: string, reason?: string) => apiFetch<any>(`/api/admin/marketplace/listings/${id}/disable`, { method: 'POST', body: JSON.stringify({ reason }) }),
};
